import is from '@sindresorhus/is';
import { useCallback } from 'react';
import Maybe from 'true-myth/maybe';

import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { JobProfileSkill } from '~/shared/data-contract/skill';

import type { SkillsDetectionResult } from '../../legacy/position/api/redetect-skills-request';
import { useMixpanelTracking } from '../../tracking/mixpanel/use-mixpanel-tracking';
import { useRedetectSkills } from '../api/use-redetect-skills';
import { useJsaTrackingContext } from '../hooks/analytics/use-jsa-tracking-context';
import type { PendingSuggestedSkill, PendingSuggestedSkillsRecord } from './skill-action-store';
import { useSkillActionStore } from './use-skill-action-store';

export type UseSkillActionsProps = {
    readonly architectureId: string;
    readonly currentSkills: readonly JobProfileSkill[];
    readonly onAcceptSkillSuggestions: (skills: readonly JobProfileSkill[]) => void;
    readonly profile: JobProfileComponent;
};

export type SkillActionsResult = {
    readonly discardPendingSkillSuggestions: () => void;
    readonly error?: Error | null;
    readonly executePendingSkillSuggestions: () => void;
    readonly handleRedetectSkills: (adjustPrompt: string) => Promise<void>;
    readonly isGenerating: boolean;
    readonly pendingSkillSuggestions: Maybe<PendingSuggestedSkillsRecord>;
    readonly toggleAcceptanceOfPendingSuggestedSkill: (pendingSkillSuggestion: PendingSuggestedSkill) => void;
};

export const createPendingSuggestedSkills = (skills: SkillsDetectionResult): Maybe<PendingSuggestedSkillsRecord> => {
    const { addedSkills, changedSkills, removedSkills } = skills;

    const result: PendingSuggestedSkillsRecord = {
        add: addedSkills.map((skill) => ({ isAccepted: true, skill, type: 'add' })),
        adjust: changedSkills.map((skill) => ({ isAccepted: true, skill, type: 'adjust' })),
        remove: removedSkills.map((skill) => ({ isAccepted: true, skill, type: 'remove' })),
    };

    const resultHasNoSkills = Object.values(result).every((skills) => skills.length === 0);

    return resultHasNoSkills ? Maybe.nothing() : Maybe.just(result);
};

const executeSkillSuggestions = (
    baseSkills: readonly JobProfileSkill[],
    suggestions: readonly PendingSuggestedSkill[],
): readonly JobProfileSkill[] => {
    return suggestions.reduce((currentSkills, suggestion) => {
        switch (suggestion.type) {
            case 'add':
                // biome-ignore lint/performance/noAccumulatingSpread: suppressed due to migration
                return [...currentSkills, suggestion.skill];
            case 'remove':
                return currentSkills.filter((skill) => skill.id !== suggestion.skill.id);
            case 'adjust':
                return currentSkills.map((skill) => (skill.id === suggestion.skill.id ? suggestion.skill : skill));
            default:
                return currentSkills;
        }
    }, baseSkills);
};

export const useSkillActions = ({
    architectureId,
    currentSkills,
    onAcceptSkillSuggestions,
    profile,
}: UseSkillActionsProps): SkillActionsResult => {
    const discardPendingSkillSuggestions = useSkillActionStore((state) => state.discardPendingSkillSuggestions);
    const pendingSkillSuggestions = useSkillActionStore((state) => state.pendingSkillSuggestions);
    const setPendingSkillSuggestions = useSkillActionStore((state) => state.setPendingSkillSuggestions);
    const toggleAcceptanceOfPendingSuggestedSkill = useSkillActionStore(
        (state) => state.toggleAcceptanceOfPendingSuggestedSkill,
    );

    const { error, isPending: isGenerating, mutateAsync: redetectSkills } = useRedetectSkills();
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();

    const handleRedetectSkills = useCallback(
        async (adjustPrompt: string): Promise<void> => {
            if (!isGenerating && adjustPrompt.trim() !== '') {
                trackEvent({
                    name: 'ai-action-used',
                    payload: {
                        ...trackingContext,
                        actionScope: 'skills',
                        actionType: 'adjust_skills',
                        architectureId,
                    },
                });
                const result = await redetectSkills({
                    additionalPrompt: Maybe.just(adjustPrompt),
                    architectureId,
                    detectionType: 'adjust',
                    jobProfile: {
                        ...profile,
                        skills: currentSkills.filter((skill) => !is.nullOrUndefined(skill.level)),
                    },
                    jobProfileId: profile.id,
                });

                const pendingSuggestions = createPendingSuggestedSkills(result);
                setPendingSkillSuggestions(pendingSuggestions);
            }
        },
        [
            isGenerating,
            profile,
            currentSkills,
            redetectSkills,
            setPendingSkillSuggestions,
            trackEvent,
            trackingContext,
            architectureId,
        ],
    );

    const executePendingSkillSuggestions = useCallback(() => {
        if (pendingSkillSuggestions.isNothing) {
            return;
        }

        const suggestionsToExecute = Object.values(pendingSkillSuggestions.value)
            .flat()
            .filter((suggestion) => suggestion.isAccepted);

        if (suggestionsToExecute.length === 0) {
            return;
        }

        const processedSkills = executeSkillSuggestions(currentSkills, suggestionsToExecute);
        onAcceptSkillSuggestions(processedSkills);

        setPendingSkillSuggestions(Maybe.nothing());
    }, [pendingSkillSuggestions, currentSkills, onAcceptSkillSuggestions, setPendingSkillSuggestions]);

    return {
        discardPendingSkillSuggestions,
        error,
        executePendingSkillSuggestions,
        handleRedetectSkills,
        isGenerating,
        pendingSkillSuggestions,
        toggleAcceptanceOfPendingSuggestedSkill,
    };
};
